import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle2, XCircle, Clock, FileText, Search, Filter, RefreshCw, History, X, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/DatePicker';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import TaskDetailModal from '../components/TaskDetailModal';
import { queueTaskEditHandoff } from '../utils/taskEditHandoff';
import { useNavigate } from 'react-router-dom';
import { getBrandBadgeClasses } from '../utils/brandColors';
import { TaskIdBadge } from '../components/TaskIdBadge';
import { getContentTypeBadgeClasses, formatContentType } from '../utils/contentTypes';
import { useBrands } from '../utils/brands';
import { useTaskOptions, canonicalKeyForName, nameForCanonicalKey } from '../utils/taskOptions';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { sortTasksByTabStatus, getOrderingLabel } from '../utils/taskOrdering';
import { matchesTaskSearch } from '../utils/taskSearch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const APPROVAL_STATUS_ICONS = { approved: '✅', standby: '⏸️', rejected: '❌' };

const ManagerApproval = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const BRANDS = useBrands();
  const { CATEGORIES: TASK_CATEGORIES, CONTENT_TYPES, TASK_STATUSES, APPROVAL_STATUSES, RAW: TASK_OPTIONS_RAW } = useTaskOptions();
  const approvalKey = (name) => canonicalKeyForName(TASK_OPTIONS_RAW.manager_approval_statuses, name);
  const DEFAULT_APPROVAL_STATUS = nameForCanonicalKey(TASK_OPTIONS_RAW.manager_approval_statuses, 'approved', APPROVAL_STATUSES[0] || 'Approved');
  const [pendingTasks, setPendingTasks] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [approvalData, setApprovalData] = useState({
    status: DEFAULT_APPROVAL_STATUS,
    notes: ''
  });
  // When a task has BOTH a pending Developer approval AND a pending/standby
  // UI/UX approval at once, the manager must explicitly pick which request
  // this dialog session is deciding on (they're independent workflows).
  const [approvalTarget, setApprovalTarget] = useState(null); // 'developer' | 'uiux' | 'generic'
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterBrand, setFilterBrand] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterUser, setFilterUser] = useState([]);
  const [filterContentType, setFilterContentType] = useState([]);
  const [filterApprovalStatus, setFilterApprovalStatus] = useState([]);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadPendingTasks(), loadApprovalHistory(), loadUsers()]);
    setLoading(false);
  };

  const loadPendingTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingTasks(response.data);
    } catch (error) {
      toast.error('Failed to load pending approval tasks');
    }
  };

  const loadApprovalHistory = async () => {
    try {
      // Load all tasks and filter for recently decided approvals (last 7 days).
      // Covers THREE workflows: SEO/User manager approvals (manager_approval_*),
      // Developer Queue approvals (developer_approval_*), and UI/UX Queue
      // approvals (uiux_approval_*). Each history row is normalised into
      // _approval_status / _approval_date / _approval_notes / _approved_by so
      // the render + filters work uniformly regardless of origin.
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const sevenDaysAgo = subDays(new Date(), 7);
      const DECIDED_KEYS = ['approved', 'rejected', 'standby'];

      const recentApprovals = response.data.map(task => {
        const isMgr = !!task.manager_approval_required && DECIDED_KEYS.includes(approvalKey(task.manager_approval_status));
        const isDev = !!task.developer_dependency && ['Approved', 'Rejected'].includes(task.developer_approval_status);
        const isUx = !!task.uiux_dependency && ['Approved', 'Rejected'].includes(task.uiux_approval_status);
        if (!isMgr && !isDev && !isUx) return null;

        let status = null, notes = '', date = null, approvedBy = null, origin = null;
        if (isMgr) {
          status = task.manager_approval_status;
          notes = task.manager_notes || '';
          date = task.manager_approval_at || task.updated_at;
          approvedBy = task.manager_approval_by || task.updated_by;
          origin = 'SEO / User';
        }
        if (isDev) {
          const devDate = task.developer_approval_at || task.developer_end_date || task.updated_at;
          // If a task went through both flows, surface whichever was decided most recently.
          if (!isMgr || (devDate && date && new Date(devDate) >= new Date(date))) {
            status = task.developer_approval_status;
            notes = task.developer_manager_notes || '';
            date = devDate;
            approvedBy = task.developer_approval_by || task.updated_by;
            origin = 'Developer';
          }
        }
        if (isUx) {
          const uxDate = task.uiux_approval_at || task.design_end_date || task.updated_at;
          // Same "most recent decision wins" rule, across all three flows.
          if ((!isMgr && !isDev) || (uxDate && date && new Date(uxDate) >= new Date(date))) {
            status = task.uiux_approval_status;
            notes = task.uiux_manager_notes || '';
            date = uxDate;
            approvedBy = task.uiux_approval_by || task.updated_by;
            origin = 'UI/UX';
          }
        }
        return { ...task, _approval_status: status, _approval_notes: notes, _approval_date: date, _approved_by: approvedBy, _approval_origin: origin };
      })
      .filter(Boolean)
      .filter(task => {
        const d = task._approval_date ? new Date(task._approval_date) : null;
        return d && d >= sevenDaysAgo;
      });

      // Note: ordering is applied centrally via the unified taskOrdering util
      // (Completed-tab rule = most recently completed first). See `sortedHistory`.
      setApprovalHistory(recentApprovals);
    } catch (error) {
      console.error('Failed to load approval history');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unknown User';
  };

  const openApprovalDialog = (task) => {
    setSelectedTask(task);
    const pendingTracks = [
      isDeveloperApproval(task) && 'developer',
      isUiuxApproval(task) && 'uiux',
      isGenericApproval(task) && 'generic',
    ].filter(Boolean);
    const singleTarget = pendingTracks.length === 1 ? pendingTracks[0] : null;
    setApprovalTarget(singleTarget);
    setApprovalData({
      status: DEFAULT_APPROVAL_STATUS,
      notes: notesForTarget(task, singleTarget)
    });
    setIsApprovalDialogOpen(true);
  };

  const isDeveloperApproval = (task) => task?.developer_approval_status === 'Pending';
  const isUiuxApproval = (task) => ['Pending', 'Standby'].includes(task?.uiux_approval_status);
  // The original/generic manager-approval track — independent of (and can
  // coexist with) the Developer and UI/UX completion-approval tracks above.
  // A task owner opts into this via the "Require Manager Approval" checkbox
  // on the task form, regardless of whether it also has a dev/uiux request.
  const isGenericApproval = (task) => !!task?.manager_approval_required && ['Pending', 'Standby'].includes(task?.manager_approval_status);

  // Prefill the notes box with whatever manager notes already exist for the
  // track being decided on — empty until a target is actually chosen when
  // more than one track is pending simultaneously.
  const notesForTarget = (task, target) => {
    if (target === 'uiux') return task.uiux_manager_notes || '';
    if (target === 'developer' || target === 'generic') return task.manager_notes || '';
    return '';
  };

  const handleApproval = async () => {
    if (!selectedTask) return;
    if (approvalTarget === null) {
      toast.error('Choose which request to decide on (Developer or UI/UX) first.');
      return;
    }

    try {
      // Developer approvals use a separate endpoint with only approve/reject (no standby).
      if (approvalTarget === 'developer') {
        if (approvalKey(approvalData.status) === 'standby') {
          toast.error('Developer approvals do not support standby. Choose Approve or Reject.');
          return;
        }
        const devAction = approvalKey(approvalData.status) === 'approved' ? 'approve' : 'reject';
        await axios.put(
          `${API}/tasks/${selectedTask.id}/developer-approval`,
          { action: devAction, manager_notes: approvalData.notes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Developer task ${devAction === 'approve' ? 'approved' : 'rejected'}`);
        setIsApprovalDialogOpen(false);
        loadData();
        return;
      }

      // UI/UX completion approvals use their own endpoint (approve/reject/standby).
      if (approvalTarget === 'uiux') {
        const uiuxActionMap = { approved: 'approve', standby: 'standby', rejected: 'reject' };
        const uiuxAction = uiuxActionMap[approvalKey(approvalData.status)];
        if (uiuxAction === 'reject' && !approvalData.notes?.trim()) {
          toast.error('A rejection reason is required.');
          return;
        }
        await axios.put(
          `${API}/tasks/${selectedTask.id}/uiux-approval`,
          { action: uiuxAction, manager_notes: approvalData.notes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const uiuxMessage = uiuxAction === 'standby'
          ? 'UI/UX completion put on standby.'
          : uiuxAction === 'reject'
          ? 'UI/UX completion rejected.'
          : 'UI/UX completion approved!';
        toast.success(uiuxMessage);
        setIsApprovalDialogOpen(false);
        loadData();
        return;
      }

      // Map the selected Approval Status (dynamic display name) to the fixed
      // backend action verb via its immutable canonical_key — survives renames.
      const actionMap = {
        approved: 'approve',
        standby: 'standby',
        rejected: 'reject'
      };
      const action = actionMap[approvalKey(approvalData.status)];
      
      await axios.put(
        `${API}/tasks/${selectedTask.id}/manager-approval`,
        {
          action: action,
          manager_notes: approvalData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const successMessage = approvalKey(approvalData.status) === 'standby'
        ? 'Task put on standby successfully!' 
        : `Task ${approvalData.status.toLowerCase()} successfully!`;
      toast.success(successMessage);
      setIsApprovalDialogOpen(false);
      loadData(); // Reload both pending and history
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update approval status');
    }
  };

  const handleResumeUiux = async () => {
    if (!selectedTask) return;
    try {
      await axios.put(
        `${API}/tasks/${selectedTask.id}/uiux-approval`,
        { action: 'resume' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Moved back to Pending Approval.');
      setIsApprovalDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume request');
    }
  };

  // Filter logic for pending tasks
  const filteredPendingTasks = useMemo(() => {
    return pendingTasks.filter(task => {
      const matchesSearch = matchesTaskSearch(task, searchTerm);
      const matchesCategory = filterCategory.length === 0 || filterCategory.includes(task.category);
      const matchesBrand = filterBrand.length === 0 || filterBrand.includes(task.brand);
      const matchesStatus = filterStatus.length === 0 || filterStatus.includes(task.your_status);
      const matchesUser = filterUser.length === 0 || filterUser.includes(task.task_done_by);
      const matchesContentType = filterContentType.length === 0 || filterContentType.includes(task.content_type || '');
      
      // Date range filter
      let matchesDateRange = true;
      if (filterFromDate || filterToDate) {
        const taskDate = task.start_date ? new Date(task.start_date) : null;
        if (taskDate) {
          const fromDate = filterFromDate ? new Date(filterFromDate) : null;
          const toDate = filterToDate ? new Date(filterToDate) : null;
          
          if (fromDate && toDate) {
            matchesDateRange = taskDate >= fromDate && taskDate <= toDate;
          } else if (fromDate) {
            matchesDateRange = taskDate >= fromDate;
          } else if (toDate) {
            matchesDateRange = taskDate <= toDate;
          }
        }
      }
      
      return matchesSearch && matchesCategory && matchesBrand && matchesStatus && matchesUser && matchesContentType && matchesDateRange;
    });
  }, [pendingTasks, searchTerm, filterCategory, filterBrand, filterStatus, filterUser, filterContentType, filterFromDate, filterToDate]);

  // Filter logic for approval history
  const filteredHistory = useMemo(() => {
    return approvalHistory.filter(task => {
      const matchesSearch = matchesTaskSearch(task, searchTerm);
      const matchesCategory = filterCategory.length === 0 || filterCategory.includes(task.category);
      const matchesBrand = filterBrand.length === 0 || filterBrand.includes(task.brand);
      const matchesStatus = filterStatus.length === 0 || filterStatus.includes(task.your_status);
      const matchesUser = filterUser.length === 0 || filterUser.includes(task.task_done_by);
      const matchesContentType = filterContentType.length === 0 || filterContentType.includes(task.content_type || '');
      const matchesApprovalStatus = filterApprovalStatus.length === 0 || filterApprovalStatus.includes(task._approval_status);
      
      // Date range filter (using the normalised approval date)
      let matchesDateRange = true;
      if (filterFromDate || filterToDate) {
        const approvalDate = task._approval_date ? new Date(task._approval_date) : null;
        if (approvalDate) {
          const fromDate = filterFromDate ? new Date(filterFromDate) : null;
          const toDate = filterToDate ? new Date(filterToDate) : null;
          
          if (fromDate && toDate) {
            matchesDateRange = approvalDate >= fromDate && approvalDate <= toDate;
          } else if (fromDate) {
            matchesDateRange = approvalDate >= fromDate;
          } else if (toDate) {
            matchesDateRange = approvalDate <= toDate;
          }
        }
      }
      
      return matchesSearch && matchesCategory && matchesBrand && matchesStatus && matchesUser && matchesContentType && matchesApprovalStatus && matchesDateRange;
    });
  }, [approvalHistory, searchTerm, filterCategory, filterBrand, filterStatus, filterUser, filterContentType, filterApprovalStatus, filterFromDate, filterToDate]);

  // Apply unified workflow-priority sorting (see utils/taskOrdering.js).
  // Pending Approval queue → Pending Approval rule (recently updated first).
  // Approval History → Completed rule (recently completed first; uses manager_approval_at
  // when present, otherwise falls back to updated_at).
  const APPROVAL_COMPLETED_FIELDS = ['_approval_date', 'manager_approval_at', 'developer_approval_at', 'updated_at'];
  const sortedPendingTasks = useMemo(
    () => sortTasksByTabStatus(filteredPendingTasks, 'Pending Approval'),
    [filteredPendingTasks]
  );
  const sortedHistory = useMemo(
    () => sortTasksByTabStatus(filteredHistory, 'Completed', { completedDateFields: APPROVAL_COMPLETED_FIELDS }),
    [filteredHistory]
  );

  const hasActiveFilters = searchTerm || filterCategory.length > 0 || filterBrand.length > 0 || 
    filterStatus.length > 0 || filterUser.length > 0 || filterContentType.length > 0 || filterApprovalStatus.length > 0 ||
    filterFromDate || filterToDate;

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterCategory([]);
    setFilterBrand([]);
    setFilterStatus([]);
    setFilterUser([]);
    setFilterContentType([]);
    setFilterApprovalStatus([]);
    setFilterFromDate('');
    setFilterToDate('');
    toast.success('All filters cleared');
  };

  const getStatusColor = (status) => {
    switch (approvalKey(status)) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'standby': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getTaskStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'In Process': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'On Hold': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technical SEO': 'bg-purple-100 text-purple-700 border-purple-200',
      'On-Page': 'bg-blue-100 text-blue-700 border-blue-200',
      'Off-Page': 'bg-pink-100 text-pink-700 border-pink-200',
      'UI/UX Design': 'bg-indigo-100 text-indigo-700 border-indigo-200',
      'SEO': 'bg-green-100 text-green-700 border-green-200',
      'QA/Technical': 'bg-orange-100 text-orange-700 border-orange-200',
      'Google Ads': 'bg-red-100 text-red-700 border-red-200',
      'Meta Ads': 'bg-cyan-100 text-cyan-700 border-cyan-200',
      'Others': 'bg-slate-100 text-slate-700 border-slate-200',
      'Technical': 'bg-purple-100 text-purple-700 border-purple-200',
      'OnPage': 'bg-blue-100 text-blue-700 border-blue-200',
      'OffPage': 'bg-pink-100 text-pink-700 border-pink-200'
    };
    return colors[category] || colors['Others'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="manager-approval-board">
      {/* Header */}
      <div className="page-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="page-title">Manager Approval Queue</h1>
            <p className="page-subtitle">Review and approve tasks requiring manager approval</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-cyan-50 border-cyan-300' : ''}
            >
              <Filter size={16} className="mr-2" />
              Filters {hasActiveFilters && <span className="ml-1 bg-cyan-500 text-white text-xs px-1.5 py-0.5 rounded-full">{
                [!!searchTerm, filterCategory.length > 0, filterBrand.length > 0, filterStatus.length > 0, 
                 filterUser.length > 0, filterContentType.length > 0, filterApprovalStatus.length > 0, !!filterFromDate, !!filterToDate]
                .filter(Boolean).length
              }</span>}
            </Button>
            <Button variant="outline" size="sm" onClick={loadData}>
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <Filter size={18} />
              Advanced Filters
            </h3>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-red-600 hover:bg-red-50">
                <X size={14} className="mr-1" />
                Clear All
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  type="text"
                  placeholder="Search tasks or Task ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-tasks"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Category</Label>
              <MultiSelectFilter
                label="Category"
                placeholder="All Categories"
                options={TASK_CATEGORIES}
                selectedValues={filterCategory}
                onChange={setFilterCategory}
                testId="approval-category-filter"
              />
            </div>
            
            {/* Brand Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Brand</Label>
              <MultiSelectFilter
                label="Brand"
                placeholder="All Brands"
                options={BRANDS}
                selectedValues={filterBrand}
                onChange={setFilterBrand}
                testId="approval-brand-filter"
              />
            </div>
            
            {/* Task Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Task Status</Label>
              <MultiSelectFilter
                label="Task Status"
                placeholder="All Statuses"
                options={TASK_STATUSES}
                selectedValues={filterStatus}
                onChange={setFilterStatus}
                testId="approval-status-filter"
              />
            </div>
            
            {/* User Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Assigned To</Label>
              <MultiSelectFilter
                label="Assigned To"
                placeholder="All Users"
                options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
                selectedValues={filterUser}
                onChange={setFilterUser}
                testId="approval-user-filter"
              />
            </div>

            {/* Content Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">Content Type</Label>
              <MultiSelectFilter
                label="Content Type"
                placeholder="All Content Types"
                options={CONTENT_TYPES}
                selectedValues={filterContentType}
                onChange={setFilterContentType}
                testId="approval-content-type-filter"
              />
            </div>
            
            {/* Approval Status Filter (only for history tab) */}
            {activeTab === 'history' && (
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Approval Status</Label>
                <MultiSelectFilter
                  label="Approval Status"
                  placeholder="All"
                  options={APPROVAL_STATUSES.filter(s => ['approved', 'standby', 'rejected'].includes(approvalKey(s))).map(s => ({ value: s, label: `${APPROVAL_STATUS_ICONS[approvalKey(s)] || ''} ${s}` }))}
                  selectedValues={filterApprovalStatus}
                  onChange={setFilterApprovalStatus}
                  testId="approval-approval-status-filter"
                />
              </div>
            )}
            
            {/* Date Range */}
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">From Date</Label>
              <DatePicker
                value={filterFromDate}
                onChange={setFilterFromDate}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-slate-600">To Date</Label>
              <DatePicker
                value={filterToDate}
                onChange={setFilterToDate}
              />
            </div>
          </div>

          <ActiveFilterChips
            chips={[
              filterCategory.length > 0 && { key: 'category', label: `${filterCategory.length} Categor${filterCategory.length > 1 ? 'ies' : 'y'}`, onRemove: () => setFilterCategory([]) },
              filterBrand.length > 0 && { key: 'brand', label: `${filterBrand.length} Brand${filterBrand.length > 1 ? 's' : ''}`, onRemove: () => setFilterBrand([]) },
              filterStatus.length > 0 && { key: 'status', label: `${filterStatus.length} Status${filterStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterStatus([]) },
              filterUser.length > 0 && { key: 'user', label: `${filterUser.length} User${filterUser.length > 1 ? 's' : ''}`, onRemove: () => setFilterUser([]) },
              filterContentType.length > 0 && { key: 'content-type', label: `${filterContentType.length} Content Type${filterContentType.length > 1 ? 's' : ''}`, onRemove: () => setFilterContentType([]) },
              filterApprovalStatus.length > 0 && { key: 'approval-status', label: `${filterApprovalStatus.length} Approval Status${filterApprovalStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterApprovalStatus([]) },
            ].filter(Boolean)}
            onClearAll={hasActiveFilters ? clearAllFilters : null}
          />
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'pending'
                ? 'bg-yellow-50 text-yellow-700 border-b-2 border-yellow-500'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Clock size={18} className="inline mr-2" />
            Pending Approval
            <span className="ml-2 bg-yellow-200 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
              {filteredPendingTasks.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'history'
                ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            <History size={18} className="inline mr-2" />
            Approval History (Last 7 Days)
            <span className="ml-2 bg-green-200 text-green-800 text-xs px-2 py-0.5 rounded-full">
              {filteredHistory.length}
            </span>
          </button>
        </div>

        <div className="p-6">
          {/* Pending Approval Tab */}
          {activeTab === 'pending' && (
            <>
              {filteredPendingTasks.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Clock size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">
                    {hasActiveFilters ? 'No tasks match your filters' : 'No tasks pending approval'}
                  </p>
                  <p className="text-sm mt-2">
                    {hasActiveFilters ? 'Try adjusting your filter criteria' : 'All tasks requiring manager approval have been reviewed'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-end mb-3">
                    <span
                      data-testid="manager-pending-sort-indicator"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-yellow-50 text-yellow-700 border border-yellow-200 font-medium"
                      title="Default ordering applied to this tab"
                    >
                      Sorted: {getOrderingLabel('Pending Approval')}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sortedPendingTasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => {
                        setSelectedTask(task);
                        setIsDetailOpen(true);
                      }}
                      data-testid={`approval-card-${task.id}`}
                      className="bg-slate-50 rounded-lg p-4 border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-2 mb-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getCategoryColor(task.category)}`}>
                          {task.category}
                        </span>
                        {task.brand && (
                          <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getBrandBadgeClasses(task.brand)}`}>
                            {task.brand}
                          </span>
                        )}
                        {isDeveloperApproval(task) && (
                          <span
                            data-testid={`approval-card-dev-badge-${task.id}`}
                            className="text-xs px-2 py-1 rounded-md font-semibold bg-amber-100 text-amber-700 border border-amber-200"
                            title="Developer completion awaiting approval"
                          >
                            Developer
                          </span>
                        )}
                        {isUiuxApproval(task) && (
                          <span
                            data-testid={`approval-card-uiux-badge-${task.id}`}
                            className="text-xs px-2 py-1 rounded-md font-semibold bg-purple-100 text-purple-700 border border-purple-200"
                            title="UI/UX completion awaiting approval"
                          >
                            UI/UX
                          </span>
                        )}
                        {isGenericApproval(task) && (
                          <span
                            data-testid={`approval-card-generic-badge-${task.id}`}
                            className="text-xs px-2 py-1 rounded-md font-semibold bg-sky-100 text-sky-700 border border-sky-200"
                            title="Manager approval requested for this task"
                          >
                            Manager Approval
                          </span>
                        )}
                        {task.content_type && (
                          <span
                            data-testid={`approval-card-content-type-${task.id}`}
                            className={`text-xs px-2 py-1 rounded-md font-semibold ${getContentTypeBadgeClasses(task.content_type)}`}
                          >
                            {task.content_type}
                          </span>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getTaskStatusColor(task.your_status)}`}>
                          {task.your_status}
                        </span>
                        {(task.manager_approval_status === 'Standby' || task.uiux_approval_status === 'Standby') && (
                          <span className="text-xs px-2 py-1 rounded-md font-semibold border bg-amber-100 text-amber-700 border-amber-200 flex items-center gap-1">
                            <Clock size={10} />
                            On Standby
                          </span>
                        )}
                      </div>

                      <h4 className="font-medium text-slate-900 mb-2 line-clamp-2">
                        <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                        {task.task}
                      </h4>

                      {task.task_done_by && (
                        <p className="text-xs text-slate-600 mb-2">
                          👤 {getUserName(task.task_done_by)}
                        </p>
                      )}

                      {task.start_date && (
                        <p className="text-xs text-slate-500 mb-3">
                          📅 {format(new Date(task.start_date), 'MMM dd, yyyy')}
                        </p>
                      )}

                      {task.notes && (
                        <div className="bg-white p-2 rounded text-xs text-slate-600 mb-3 line-clamp-2">
                          <FileText size={12} className="inline mr-1" />
                          {task.notes}
                        </div>
                      )}

                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          openApprovalDialog(task);
                        }}
                        className="w-full btn-cta"
                        data-testid={`approve-button-${task.id}`}
                      >
                        Review & Approve
                      </Button>
                    </div>
                  ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Approval History Tab */}
          {activeTab === 'history' && (
            <>
              {filteredHistory.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <History size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">
                    {hasActiveFilters ? 'No tasks match your filters' : 'No approval history in the last 7 days'}
                  </p>
                  <p className="text-sm mt-2">
                    {hasActiveFilters ? 'Try adjusting your filter criteria' : 'Approved or rejected tasks will appear here'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-end mb-3">
                    <span
                      data-testid="manager-history-sort-indicator"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-green-50 text-green-700 border border-green-200 font-medium"
                      title="Default ordering applied to this tab"
                    >
                      Sorted: {getOrderingLabel('Completed')}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Task</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Category</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Brand</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Content Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Assigned To</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Approval Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Approved By</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Approval Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Manager Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHistory.map((task) => (
                        <tr 
                          key={task.id} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => {
                            setSelectedTask(task);
                            setIsDetailOpen(true);
                          }}
                        >
                          <td className="px-4 py-3">
                            <p className="font-medium text-slate-900 line-clamp-2 max-w-xs">
                              <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                              {task.task}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getCategoryColor(task.category)}`}>
                              {task.category}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {task.brand ? (
                              <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getBrandBadgeClasses(task.brand)}`}>
                                {task.brand}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {task.content_type ? (
                              <span
                                data-testid={`approval-history-content-type-${task.id}`}
                                className={`text-xs px-2 py-1 rounded-md font-semibold ${getContentTypeBadgeClasses(task.content_type)}`}
                              >
                                {task.content_type}
                              </span>
                            ) : (
                              <span className="text-slate-400">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {task.task_done_by ? getUserName(task.task_done_by) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-semibold border ${getStatusColor(task._approval_status)}`}>
                              {task._approval_status === 'Approved' ? <CheckCircle2 size={12} /> : 
                               task._approval_status === 'Standby' ? <Clock size={12} /> : 
                               <XCircle size={12} />}
                              {task._approval_status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {task._approved_by ? getUserName(task._approved_by) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {task._approval_date ? (
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {format(new Date(task._approval_date), 'MMM dd, yyyy HH:mm')}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            {task._approval_notes ? (
                              <span className="text-xs text-slate-600 line-clamp-2 max-w-xs" title={task._approval_notes}>
                                {task._approval_notes}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={isApprovalDialogOpen} onOpenChange={setIsApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manager Approval</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-900 mb-2">
                  <TaskIdBadge taskId={selectedTask.task_id} className="mr-1.5 align-middle" />
                  {selectedTask.task}
                </h3>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getCategoryColor(selectedTask.category)}`}>
                    {selectedTask.category}
                  </span>
                  {selectedTask.brand && (
                    <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getBrandBadgeClasses(selectedTask.brand)}`}>
                      {selectedTask.brand}
                    </span>
                  )}
                  {isDeveloperApproval(selectedTask) && (
                    <span
                      data-testid="approval-modal-dev-badge"
                      className="text-xs px-2 py-1 rounded-md font-semibold bg-amber-100 text-amber-700 border border-amber-200"
                    >
                      Developer Approval
                    </span>
                  )}
                  {isUiuxApproval(selectedTask) && (
                    <span
                      data-testid="approval-modal-uiux-badge"
                      className="text-xs px-2 py-1 rounded-md font-semibold bg-purple-100 text-purple-700 border border-purple-200"
                    >
                      UI/UX Approval{selectedTask.uiux_approval_status === 'Standby' ? ' (On Standby)' : ''}
                    </span>
                  )}
                  {isGenericApproval(selectedTask) && (
                    <span
                      data-testid="approval-modal-generic-badge"
                      className="text-xs px-2 py-1 rounded-md font-semibold bg-sky-100 text-sky-700 border border-sky-200"
                    >
                      Manager Approval{selectedTask.manager_approval_status === 'Standby' ? ' (On Standby)' : ''}
                    </span>
                  )}
                  {selectedTask.content_type && (
                    <span
                      data-testid="approval-modal-content-type-badge"
                      className={`text-xs px-2 py-1 rounded-md font-semibold ${getContentTypeBadgeClasses(selectedTask.content_type)}`}
                    >
                      {selectedTask.content_type}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-1 rounded-md font-semibold border ${getTaskStatusColor(selectedTask.your_status)}`}>
                    {selectedTask.your_status}
                  </span>
                </div>
                {approvalTarget === null && (
                  <div className="flex items-center gap-2 flex-wrap bg-amber-50 border border-amber-200 rounded-md p-2 mb-2" data-testid="approval-target-picker">
                    <span className="text-xs font-medium text-amber-800">This task has more than one request pending. Decide on:</span>
                    {isDeveloperApproval(selectedTask) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setApprovalTarget('developer'); setApprovalData(d => ({ ...d, notes: notesForTarget(selectedTask, 'developer') })); }} data-testid="approval-target-developer">
                        Developer
                      </Button>
                    )}
                    {isUiuxApproval(selectedTask) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setApprovalTarget('uiux'); setApprovalData(d => ({ ...d, notes: notesForTarget(selectedTask, 'uiux') })); }} data-testid="approval-target-uiux">
                        UI/UX
                      </Button>
                    )}
                    {isGenericApproval(selectedTask) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setApprovalTarget('generic'); setApprovalData(d => ({ ...d, notes: notesForTarget(selectedTask, 'generic') })); }} data-testid="approval-target-generic">
                        Manager Approval
                      </Button>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-slate-600 mt-3">
                  <p>
                    <span className="text-slate-500">Category:</span>{' '}
                    <span className="font-medium">{selectedTask.category || 'N/A'}</span>
                  </p>
                  <p>
                    <span className="text-slate-500">Brand:</span>{' '}
                    <span className="font-medium">{selectedTask.brand || 'N/A'}</span>
                  </p>
                  <p data-testid="approval-modal-content-type-field">
                    <span className="text-slate-500">Content Type:</span>{' '}
                    <span className="font-medium">{formatContentType(selectedTask.content_type)}</span>
                  </p>
                  <p>
                    <span className="text-slate-500">Status:</span>{' '}
                    <span className="font-medium">{selectedTask.your_status || 'N/A'}</span>
                  </p>
                </div>
                {selectedTask.task_done_by && (
                  <p className="text-sm text-slate-600 mt-2">
                    Assigned to: <span className="font-medium">{getUserName(selectedTask.task_done_by)}</span>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Approval Status</Label>
                <Select value={approvalData.status} onValueChange={(value) => setApprovalData({...approvalData, status: value})}>
                  <SelectTrigger data-testid="approval-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {APPROVAL_STATUSES.filter(s => approvalKey(s) === 'approved').map(s => (
                      <SelectItem key={s} value={s}>{APPROVAL_STATUS_ICONS.approved} {s}</SelectItem>
                    ))}
                    {approvalTarget !== 'developer' && APPROVAL_STATUSES.filter(s => approvalKey(s) === 'standby').map(s => (
                      <SelectItem key={s} value={s}>{APPROVAL_STATUS_ICONS.standby} {s}</SelectItem>
                    ))}
                    {APPROVAL_STATUSES.filter(s => approvalKey(s) === 'rejected').map(s => (
                      <SelectItem key={s} value={s}>{APPROVAL_STATUS_ICONS.rejected} {s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Manager Notes</Label>
                <Textarea
                  value={approvalData.notes}
                  onChange={(e) => setApprovalData({...approvalData, notes: e.target.value})}
                  placeholder="Add notes about your decision..."
                  rows={4}
                  data-testid="manager-notes-input"
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button variant="outline" onClick={() => setIsApprovalDialogOpen(false)}>
                  Cancel
                </Button>
                {isUiuxApproval(selectedTask) && selectedTask.uiux_approval_status === 'Standby' && (
                  <Button
                    variant="outline"
                    onClick={handleResumeUiux}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="resume-uiux-approval-button"
                  >
                    Resume to Pending
                  </Button>
                )}
                <Button 
                  onClick={handleApproval}
                  className="btn-cta"
                  disabled={approvalTarget === null}
                  data-testid="confirm-approval-button"
                >
                  Submit Decision
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedTask(null);
        }}
        users={users}
        onEdit={(task) => {
          // Single edit dialog lives in TaskTable — hand off via session store
          // and route there. Keeps the form definition in one place across the app.
          queueTaskEditHandoff(task.id);
          setIsDetailOpen(false);
          setSelectedTask(null);
          navigate('/tasks');
        }}
      />
    </div>
  );
};

export default ManagerApproval;
